/**
 * 全ボタン共通の「押した感」。
 * 船員アプリのボタンは多くが inline style を持つため、CSS の :active では上書きされて
 * 効かない。そこで pointerdown/up を document で委譲監視し、押下中だけ inline style を
 * `!important` で一時上書きして「少し縮む＋わずかに暗く」する。既存の transform（translate 等）は
 * 保持したまま scale を足すのでレイアウトは崩れない。動的に増えるボタンにも自動で効く。
 */
const KT = "__pfTransform";
const KF = "__pfFilter";
const SCALE = "scale(0.92)";
const BRIGHT = "brightness(0.88)";

type Pressable = HTMLElement & { [KT]?: string; [KF]?: string };

let current: Pressable | null = null;

function press(el: Pressable) {
  if (el[KT] !== undefined) return;
  const s = el.style;
  el[KT] = s.transform || "";
  el[KF] = s.filter || "";
  s.setProperty("transition", "transform .06s ease, filter .06s ease", "important");
  s.setProperty("transform", (el[KT] ? el[KT] + " " : "") + SCALE, "important");
  s.setProperty("filter", (el[KF] ? el[KF] + " " : "") + BRIGHT, "important");
}

function release(el: Pressable) {
  if (el[KT] === undefined) return;
  const prevT = el[KT]!;
  const prevF = el[KF]!;
  delete el[KT];
  delete el[KF];
  const s = el.style;
  s.removeProperty("transform");
  if (prevT) s.transform = prevT;
  s.removeProperty("filter");
  if (prevF) s.filter = prevF;
  // 戻りも滑らかに見せてから transition を後始末
  window.setTimeout(() => { try { s.removeProperty("transition"); } catch { /* noop */ } }, 140);
}

function buttonFrom(t: EventTarget | null): Pressable | null {
  const el = t as HTMLElement | null;
  if (!el || typeof el.closest !== "function") return null;
  const b = el.closest("button") as HTMLButtonElement | null;
  if (!b || b.disabled) return null;
  if (b.getAttribute("aria-disabled") === "true") return null;
  return b as Pressable;
}

export function initPressFeedback(): void {
  const end = () => { if (current) { release(current); current = null; } };
  document.addEventListener("pointerdown", (e) => {
    const b = buttonFrom(e.target);
    if (current && current !== b) end();
    if (b) { press(b); current = b; }
  }, true);
  document.addEventListener("pointerup", end, true);
  document.addEventListener("pointercancel", end, true);
  // 指がボタン外へずれたら戻す（押しっぱなしの見た目を残さない）
  document.addEventListener("pointermove", (e) => {
    if (current && buttonFrom(e.target) !== current) end();
  }, true);
  window.addEventListener("blur", end);
}
